/**
 * Webhook Effects
 * Side effects for webhook operations
 */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { WebhookService } from '../services/webhook.service';
import { NotificationService } from '@core/services/notification.service';
import * as WebhookActions from './webhook.actions';

@Injectable()
export class WebhookEffects {
  private actions$ = inject(Actions);
  private webhookService = inject(WebhookService);
  private notificationService = inject(NotificationService);

  /**
   * Load Webhooks Effect
   */
  loadWebhooks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.loadWebhooks),
      switchMap(({ page = 1, pageSize = 20, isActive }) =>
        this.webhookService.getWebhooks(page, pageSize, isActive).pipe(
          map(response => WebhookActions.loadWebhooksSuccess({
            webhooks: response.webhooks,
            total: response.total,
            page: response.page,
            pageSize: response.page_size
          })),
          catchError(error => of(WebhookActions.loadWebhooksFailure({
            error: error.message || 'Failed to load webhooks'
          })))
        )
      )
    )
  );

  /**
   * Load Single Webhook Effect
   */
  loadWebhook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.loadWebhook),
      switchMap(({ webhookId }) =>
        this.webhookService.getWebhook(webhookId).pipe(
          map(webhook => WebhookActions.loadWebhookSuccess({ webhook })),
          catchError(error => of(WebhookActions.loadWebhookFailure({
            error: error.message || 'Failed to load webhook'
          })))
        )
      )
    )
  );

  /**
   * Create Webhook Effect
   */
  createWebhook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.createWebhook),
      switchMap(({ request }) =>
        this.webhookService.createWebhook(request).pipe(
          map(response => WebhookActions.createWebhookSuccess({ response })),
          catchError(error => of(WebhookActions.createWebhookFailure({
            error: error.message || 'Failed to create webhook'
          })))
        )
      )
    )
  );

  /**
   * Create Webhook Success Effect
   */
  createWebhookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.createWebhookSuccess),
      tap(({ response }) => {
        this.notificationService.showSuccess(
          'Webhook created successfully! Save the secret - it won\'t be shown again.'
        );
      })
    ),
    { dispatch: false }
  );

  /**
   * Update Webhook Effect
   */
  updateWebhook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.updateWebhook),
      switchMap(({ webhookId, request }) =>
        this.webhookService.updateWebhook(webhookId, request).pipe(
          map(webhook => WebhookActions.updateWebhookSuccess({ webhook })),
          catchError(error => of(WebhookActions.updateWebhookFailure({
            error: error.message || 'Failed to update webhook'
          })))
        )
      )
    )
  );

  /**
   * Update Webhook Success Effect
   */
  updateWebhookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.updateWebhookSuccess),
      tap(() => {
        this.notificationService.showSuccess('Webhook updated successfully');
      })
    ),
    { dispatch: false }
  );

  /**
   * Delete Webhook Effect
   */
  deleteWebhook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.deleteWebhook),
      switchMap(({ webhookId }) =>
        this.webhookService.deleteWebhook(webhookId).pipe(
          map(() => WebhookActions.deleteWebhookSuccess({ webhookId })),
          catchError(error => of(WebhookActions.deleteWebhookFailure({
            error: error.message || 'Failed to delete webhook'
          })))
        )
      )
    )
  );

  /**
   * Delete Webhook Success Effect
   */
  deleteWebhookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.deleteWebhookSuccess),
      tap(() => {
        this.notificationService.showSuccess('Webhook deleted successfully');
      })
    ),
    { dispatch: false }
  );

  /**
   * Test Webhook Effect
   */
  testWebhook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.testWebhook),
      switchMap(({ webhookId, request }) =>
        this.webhookService.testWebhook(webhookId, request).pipe(
          map(response => WebhookActions.testWebhookSuccess({ response })),
          catchError(error => of(WebhookActions.testWebhookFailure({
            error: error.message || 'Failed to test webhook'
          })))
        )
      )
    )
  );

  /**
   * Test Webhook Success Effect
   */
  testWebhookSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.testWebhookSuccess),
      tap(({ response }) => {
        if (response.success) {
          this.notificationService.showSuccess('Test webhook delivered successfully');
        } else {
          this.notificationService.showError(`Test webhook failed: ${response.error_message || 'Unknown error'}`);
        }
      })
    ),
    { dispatch: false }
  );

  /**
   * Load Webhook Deliveries Effect
   */
  loadWebhookDeliveries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WebhookActions.loadWebhookDeliveries),
      switchMap(({ webhookId, page = 1, pageSize = 20, status }) =>
        this.webhookService.getWebhookDeliveries(webhookId, page, pageSize, status).pipe(
          map(response => WebhookActions.loadWebhookDeliveriesSuccess({
            deliveries: response.deliveries,
            total: response.total,
            page: response.page,
            pageSize: response.page_size
          })),
          catchError(error => of(WebhookActions.loadWebhookDeliveriesFailure({
            error: error.message || 'Failed to load webhook deliveries'
          })))
        )
      )
    )
  );

  /**
   * Error Notification Effect
   */
  showError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        WebhookActions.loadWebhooksFailure,
        WebhookActions.loadWebhookFailure,
        WebhookActions.createWebhookFailure,
        WebhookActions.updateWebhookFailure,
        WebhookActions.deleteWebhookFailure,
        WebhookActions.testWebhookFailure,
        WebhookActions.loadWebhookDeliveriesFailure
      ),
      tap(({ error }) => {
        this.notificationService.showError(error);
      })
    ),
    { dispatch: false }
  );
}

